/**
 * NtopParametricPanel — the right-hand panel of the Control Surfaces
 * workbench's nTop MODE (FXC-9009). A picked partner .ntop notebook bypasses
 * the segment/sections pipeline entirely; this panel is everything the mode
 * offers beside the 3D pane:
 *
 *   INPUTS  — one row per declared sidecar input. The FRAMES-AXIS slider is the
 *             star: it snaps onto the baked frame values (geometry only exists
 *             AT frames — see ntopFrames.nearestFrame) and drives the viewer's
 *             mesh swap, which is near-instant because the parent preloads
 *             every frame into the shared tess cache. Non-axis inputs render
 *             DISABLED at their baked/fixed values ("fixed during bake") —
 *             changing them would need a re-bake, so pretending they are live
 *             would be a lie.
 *   QA      — mesh-QA chips for the ACTIVE frame (watertight / triangles /
 *             p95 edge-to-chord, amber above 0.05 / components), honest
 *             "no QA" when the manifest carries none.
 *   BAKE    — "Bake frames with nTopCL" sweeps the axis input's declared range
 *             (bakeAxisValues, capped) with the other scalars fixed at their
 *             defaults; progress rides the session's ntopBake status (the poll
 *             lives in csSessionStore.startNtopBake and survives unmounts).
 *             Honest note when the engine is off in this environment.
 *   SLICE   — a 0..1 eta slider + "Slice section": POST /ntop/slice on the
 *             active frame STL, the returned main-element section drawn as a
 *             compact inline SVG polyline, and "Send to High-Lift" seeding the
 *             2D workbench with the CLEAN sliced foil (clean-foil path only —
 *             a raw slice has no cut bodies/hinge/rig, so those seed fields
 *             are honest-absent; see handleSend).
 *
 * Styling follows the workbench idiom — makeControlSurfaceStyles off the V5
 * tokens + the theme-aware highlift palette, no hardcoded hexes.
 */
import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from "react";

import {
  ntopSlice,
  type NtopEngineStatus,
  type NtopFrame,
  type NtopNotebook,
  type NtopQa,
  type NtopSliceSection,
} from "../../api/ntop";
import { normalizeCleanFoil, publishSectionSeed } from "../highlift/foil/sectionSeed";
import type { SlottedSection } from "../highlift/geometry/slottedSection";
import type { HighliftPalette } from "../highlift/foil/useHighliftTheme";
import { makeControlSurfaceStyles, ParamSlider } from "./ControlSurfaceControls";
import { NTOP_ENGINE_OFF_NOTE, type BakeStatus } from "./csSessionStore";
import { axisSliderConfig, framesAxisInput, frameValues, nearestFrame } from "./ntopFrames";

/** p95 edge/chord above this reads as an under-refined mesh (amber chip). */
const EDGE_TO_CHORD_WARN = 0.05;

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

type SliceState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "done";
      sec: NtopSliceSection;
      unitMm: number;
      /** Provenance of the slice (which frame + station it came from). */
      stl: string;
      eta: number;
      frameValue: number;
    };

/** Compact inline SVG of a sliced section: the main-element outline as ONE
 *  polyline (upper TE→LE then lower LE→TE), fit-to-extents, z-up drawn up. */
function SliceSectionSvg({ sec, palette }: { sec: NtopSliceSection; palette: HighliftPalette }) {
  const W = 320;
  const H = 110;
  const PAD = 8;
  const pts: [number, number][] = [];
  for (let i = sec.upper.length - 1; i >= 0; i--) {
    const p = sec.upper[i];
    if (p && Number.isFinite(p[0]) && Number.isFinite(p[1])) pts.push([p[0], p[1]]);
  }
  for (let i = 1; i < sec.lower.length; i++) {
    const p = sec.lower[i];
    if (p && Number.isFinite(p[0]) && Number.isFinite(p[1])) pts.push([p[0], p[1]]);
  }
  if (pts.length < 8) return null;
  let x0 = Infinity;
  let x1 = -Infinity;
  let z0 = Infinity;
  let z1 = -Infinity;
  for (const [x, z] of pts) {
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (z < z0) z0 = z;
    if (z > z1) z1 = z;
  }
  const scale = Math.min((W - 2 * PAD) / Math.max(x1 - x0, 1e-9), (H - 2 * PAD) / Math.max(z1 - z0, 1e-9));
  const ox = (W - (x1 - x0) * scale) / 2;
  const oz = (H - (z1 - z0) * scale) / 2;
  const points = pts
    .map(([x, z]) => `${(ox + (x - x0) * scale).toFixed(1)},${(H - (oz + (z - z0) * scale)).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: "100%", height: "auto", display: "block", background: palette.surface, borderRadius: 7, border: `1px solid ${palette.line}` }}
      role="img"
      aria-label="Sliced 2D section"
      data-testid="cs-ntop-slice-svg"
    >
      <polyline points={points} fill="none" stroke={palette.primary} strokeWidth={1.5} />
    </svg>
  );
}

/** QA chips for the active frame, RiggingTable palette convention (ok=primary,
 *  warn=amber, bad=warn red). Neutral values keep the ink tone. */
function qaChips(qa: NtopQa, palette: HighliftPalette): Array<{ label: string; color: string; title: string }> {
  const p95c = qa.edge_to_chord_p95;
  return [
    {
      label: `watertight ${qa.watertight ? "✓" : "✗"}`,
      color: qa.watertight ? palette.primary : palette.warn,
      title: "Whether the frame STL is a closed (watertight) mesh",
    },
    {
      label: `${qa.triangles.toLocaleString()} tris`,
      color: palette.ink3,
      title: "Triangle count of the frame STL",
    },
    {
      label: `p95 edge/chord ${p95c === null ? "-" : p95c.toFixed(3)}`,
      color: p95c !== null && p95c > EDGE_TO_CHORD_WARN ? palette.amber : palette.ink3,
      title: `95th-percentile edge length over mean chord - above ${EDGE_TO_CHORD_WARN} reads as under-refined`,
    },
    {
      label: `${qa.components} component${qa.components === 1 ? "" : "s"}`,
      color: palette.ink3,
      title: "Connected-component count of the mesh",
    },
  ];
}

export interface NtopParametricPanelProps {
  palette: HighliftPalette;
  projectId: string;
  notebook: NtopNotebook;
  engine: NtopEngineStatus;
  /** Frames-axis value of the ACTIVE frame (session-held, snapped). */
  frameValue: number;
  onFrameValueChange: (v: number) => void;
  /** Frame-preload progress over the manifest (tessellations landed / total / failed). */
  framesLoaded: number;
  framesTotal: number;
  framesFailed: number;
  activeFrame: NtopFrame | null;
  bake: BakeStatus;
  onBake: () => void;
  onCancelBake: () => void;
  sliceEta: number;
  onSliceEtaChange: (v: number) => void;
  onOpenView: (view: "highlift") => void;
}

export function NtopParametricPanel({
  palette,
  projectId,
  notebook,
  engine,
  frameValue,
  onFrameValueChange,
  framesLoaded,
  framesTotal,
  framesFailed,
  activeFrame,
  bake,
  onBake,
  onCancelBake,
  sliceEta,
  onSliceEtaChange,
  onOpenView,
}: NtopParametricPanelProps) {
  const S = useMemo(() => makeControlSurfaceStyles(palette), [palette]);
  const frames = notebook.frames;
  const values = useMemo(() => frameValues(frames), [frames]);
  const axis = useMemo(() => framesAxisInput(notebook), [notebook]);
  const slider = useMemo(() => axisSliderConfig(values, axis), [values, axis]);
  const hasFrames = values.length > 0;
  const axisUnits = axis?.units ? ` ${axis.units}` : "";

  const [slice, setSlice] = useState<SliceState>({ status: "idle" });
  const [sendNote, setSendNote] = useState<string | null>(null);

  const handleSlice = useCallback(() => {
    if (!activeFrame) return;
    const eta = sliceEta;
    const stl = activeFrame.stl;
    const fv = activeFrame.value;
    setSendNote(null);
    setSlice({ status: "loading" });
    ntopSlice(projectId, { stlPath: stl, spanAxis: "y", stations: [eta] })
      .then((res) => {
        const sec = res.sections[eta.toFixed(4)] ?? Object.values(res.sections)[0] ?? null;
        if (!sec || sec.upper.length < 8 || sec.lower.length < 8) {
          setSlice({
            status: "error",
            message: "No usable section at this station - try a different eta.",
          });
          return;
        }
        const unit = typeof res.unit_mm === "number" && Number.isFinite(res.unit_mm) && res.unit_mm > 0 ? res.unit_mm : 1;
        setSlice({ status: "done", sec, unitMm: unit, stl, eta, frameValue: fv });
      })
      .catch((e) => setSlice({ status: "error", message: errorMessage(e) }));
  }, [activeFrame, projectId, sliceEta]);

  // Send to High-Lift: seed ONLY the clean-foil path. A raw slice carries no
  // cut bodies, hinge or rig, so the SlottedSection publishSectionSeed requires
  // is a MINIMAL HONEST carrier: empty elements (the two-body / exact-contour
  // modes disable themselves), NaN hinge/deflection (JSON turns NaN into null,
  // which every consumer finite-guards into "absent" — csRunAxes, seedFlapOpts,
  // sectionHingeXc), and rig.unitMm carrying the slice's TRUE unit scale so the
  // physical chord — hence Reynolds — stays honest in the 2D workbench.
  const handleSend = useCallback(() => {
    if (slice.status !== "done") return;
    const coords = normalizeCleanFoil(
      { upper: slice.sec.upper, lower: slice.sec.lower },
      slice.sec.chord,
      1, // nTop frame STLs are authored z-up; no segmenter runs here to say otherwise
    );
    if (!coords) {
      setSendNote("This section is too degenerate to seed the 2D workbench.");
      return;
    }
    const axisName = frames?.axis ?? axis?.name ?? "value";
    const name = `${notebook.label} ${axisName} ${slice.frameValue} eta ${slice.eta.toFixed(2)}`;
    const section: SlottedSection = {
      surface: "wing",
      csType: "section",
      name,
      eta: slice.eta,
      chord: slice.sec.chord,
      hinge: { x: Number.NaN, z: Number.NaN },
      deflectionDeg: Number.NaN,
      elements: [],
      rig: { unitMm: slice.unitMm },
    };
    publishSectionSeed({
      section,
      cleanFoil: { coords, name },
      note: `Seeded from nTop parametric - ${notebook.label} at ${axisName} ${slice.frameValue}, eta ${slice.eta.toFixed(2)}`,
    });
    onOpenView("highlift");
  }, [axis, frames, notebook.label, onOpenView, slice]);

  const bakeProgressPct =
    bake.status === "running" && typeof bake.progress === "number"
      ? Math.round(Math.min(1, Math.max(0, bake.progress)) * 100)
      : null;
  const chordMm =
    slice.status === "done" && Number.isFinite(slice.sec.chord)
      ? slice.sec.chord * slice.unitMm
      : null;

  /** Disabled input row: a non-axis input shown at its baked/fixed value. */
  const fixedRow = (label: string, display: string, hint: string | null) => (
    <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: palette.ink4 }}>
      <span style={{ width: 86, flex: "0 0 auto" }}>{label}</span>
      <input type="range" disabled style={{ flex: 1, minWidth: 0, opacity: 0.4 }} aria-label={label} />
      <span style={{ fontFamily: "var(--v5-font-mono, monospace)", minWidth: 62, textAlign: "right" }}>{display}</span>
      {hint ? <span style={{ fontSize: 9.5, whiteSpace: "nowrap" }}>{hint}</span> : null}
    </div>
  );

  const chip = (label: string, color: string, title: string): ReactNode => (
    <span
      key={label}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 8px",
        borderRadius: 999,
        border: `1px solid ${palette.line}`,
        background: palette.surface2,
        fontSize: 10.5,
        fontFamily: "var(--v5-font-mono, monospace)",
        color: palette.ink2,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", flex: "0 0 auto", background: color }} />
      {label}
    </span>
  );

  const mono: CSSProperties = { fontFamily: "var(--v5-font-mono, monospace)" };

  return (
    <div data-testid="cs-ntop-panel" style={{ display: "contents" }}>
      {/* --- Notebook + engine ------------------------------------------- */}
      <div style={S.sectionHead}>nTop notebook</div>
      <div style={S.section}>
        <span style={{ fontWeight: 600 }}>{notebook.label}</span>
        <span style={S.footNote} title={notebook.path}>
          {notebook.path.split(/[\\/]/).pop()} · {notebook.size_mb.toFixed(1)} MB
        </span>
        <span style={S.footNote} data-testid="cs-ntop-engine">
          engine: {engine.mode}
          {engine.detail ? ` - ${engine.detail}` : ""}
        </span>
      </div>

      {/* --- Inputs -------------------------------------------------------- */}
      <div style={S.sectionHead}>Inputs</div>
      <div style={S.section}>
        {axis && slider && hasFrames ? (
          <ParamSlider
            label={axis.label ?? axis.name}
            value={frameValue}
            min={slider.min}
            max={slider.max}
            step={slider.step}
            display={`${frameValue}${axisUnits}`}
            onChange={(v) => onFrameValueChange(nearestFrame(values, v))}
            palette={palette}
          />
        ) : axis ? (
          fixedRow(
            axis.label ?? axis.name,
            `${typeof axis.default === "number" ? axis.default : "-"}${axisUnits}`,
            "no frames yet",
          )
        ) : (
          <span style={S.noteText}>This notebook declares no scalar inputs.</span>
        )}
        {notebook.inputs
          .filter((inp) => inp.type === "scalar" && inp !== axis)
          .map((inp) => {
            const fixedValue = frames?.fixed[inp.name] ?? inp.default;
            return fixedRow(
              inp.label ?? inp.name,
              `${typeof fixedValue === "number" ? fixedValue : "-"}${inp.units ? ` ${inp.units}` : ""}`,
              hasFrames ? "fixed during bake" : null,
            );
          })}
        {notebook.inputs
          .filter((inp) => inp.type !== "scalar")
          .map((inp) => (
            <span key={inp.name} style={S.footNote}>
              {inp.label ?? inp.name}: text input (not swept)
            </span>
          ))}
        {hasFrames && frames ? (
          <span style={S.footNote}>
            {values.length} frame{values.length === 1 ? "" : "s"} along {frames.axis} ·{" "}
            {frames.source === "ntopcl" ? "baked by nTopCL" : "pre-baked (external)"}
            {frames.baked_at ? ` · ${frames.baked_at}` : ""}
          </span>
        ) : null}
        {framesTotal > 0 && framesLoaded < framesTotal ? (
          <span style={S.footNote} data-testid="cs-ntop-preload-note">
            loading frames {framesLoaded}/{framesTotal}
            {framesFailed > 0 ? ` - ${framesFailed} failed` : ""} - scrubbing works for loaded frames
          </span>
        ) : null}
      </div>

      {/* --- Mesh QA (active frame) ---------------------------------------- */}
      <div style={S.sectionHead}>Mesh QA</div>
      <div style={S.section}>
        {activeFrame ? (
          <>
            <span style={{ ...S.footNote, ...mono }}>
              frame {activeFrame.value}
              {axisUnits} · {activeFrame.size_mb.toFixed(1)} MB
            </span>
            {activeFrame.qa ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="cs-ntop-qa">
                {qaChips(activeFrame.qa, palette).map((c) => chip(c.label, c.color, c.title))}
              </div>
            ) : (
              <span style={S.noteText} data-testid="cs-ntop-qa-none">
                No QA recorded for this frame.
              </span>
            )}
          </>
        ) : (
          <span style={S.noteText}>No baked frames yet - nothing to QA.</span>
        )}
      </div>

      {/* --- Bake ----------------------------------------------------------- */}
      <div style={S.sectionHead}>Bake</div>
      <div style={S.section}>
        <button
          style={S.secondaryButton}
          onClick={onBake}
          disabled={!engine.enabled || !axis || bake.status === "running"}
          data-testid="cs-ntop-bake-button"
        >
          {bake.status === "running" ? "Baking..." : "Bake frames with nTopCL"}
        </button>
        {bake.status === "running" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              style={{ ...S.secondaryButton, padding: "2px 8px", fontSize: 11 }}
              onClick={onCancelBake}
              data-testid="cs-ntop-bake-cancel"
            >
              Cancel
            </button>
            {bakeProgressPct !== null ? (
              <span style={S.footNote} data-testid="cs-ntop-bake-progress">
                {bakeProgressPct}%
              </span>
            ) : null}
            {bake.message ? <span style={S.footNote}>{bake.message}</span> : null}
          </div>
        ) : null}
        {bake.status === "done" || bake.status === "unavailable" || bake.status === "cancelled" ? (
          <span style={S.noteText}>{bake.message}</span>
        ) : null}
        {bake.status === "error" ? <span style={S.errorText}>Bake failed - {bake.message}</span> : null}
        {!engine.enabled ? (
          <span style={S.footNote} data-testid="cs-ntop-engine-off">
            {NTOP_ENGINE_OFF_NOTE}
          </span>
        ) : axis ? (
          <span style={S.footNote}>
            Sweeps {axis.label ?? axis.name} across its declared range; other inputs stay at their
            defaults.
          </span>
        ) : null}
      </div>

      {/* --- 2D slice -------------------------------------------------------- */}
      <div style={S.sectionHead}>2D section</div>
      <div style={{ ...S.section, paddingBottom: 16 }}>
        <ParamSlider
          label="station eta"
          value={sliceEta}
          min={0}
          max={1}
          step={0.01}
          display={sliceEta.toFixed(2)}
          onChange={onSliceEtaChange}
          palette={palette}
        />
        <button
          style={S.secondaryButton}
          onClick={handleSlice}
          disabled={!activeFrame || slice.status === "loading"}
          data-testid="cs-ntop-slice-button"
        >
          {slice.status === "loading" ? "Slicing..." : "Slice section"}
        </button>
        {slice.status === "error" ? (
          <span style={S.errorText}>Slice failed - {slice.message}</span>
        ) : null}
        {slice.status === "done" ? (
          <>
            <SliceSectionSvg sec={slice.sec} palette={palette} />
            <span style={{ ...S.footNote, ...mono }}>
              eta {slice.eta.toFixed(2)} · chord{" "}
              {chordMm !== null ? `${chordMm.toFixed(0)} mm` : "-"}
              {slice.sec.extra_loops > 0
                ? ` · +${slice.sec.extra_loops} extra loop${slice.sec.extra_loops === 1 ? "" : "s"} (main element drawn)`
                : ""}
            </span>
            <button
              style={S.primaryButton}
              onClick={handleSend}
              data-testid="cs-ntop-send-button"
            >
              Send to High-Lift
            </button>
            {sendNote ? <span style={S.noteText}>{sendNote}</span> : null}
            <span style={S.footNote}>
              Seeds the 2D workbench with this CLEAN sliced section as the base foil (no cut
              bodies or hinge rig exist in a raw slice - the workbench's own devices take it from
              there).
            </span>
          </>
        ) : null}
        {!activeFrame ? (
          <span style={S.footNote}>Slicing needs a baked frame to cut.</span>
        ) : null}
      </div>
    </div>
  );
}
